import React, { useState, useEffect, useCallback } from 'react';
import Map, { Source, Layer, Marker } from 'react-map-gl';
import mapboxgl from 'mapbox-gl'; 
// Only import CSS in browser environment to avoid SSR issues
if (typeof window !== 'undefined') {
  require('mapbox-gl/dist/mapbox-gl.css');
}
import { types as sdkTypes } from '../../../../util/sdkLoader';
import css from './ClusterMap.module.css';

const { LatLng } = sdkTypes;

const MAPBOX_TOKEN = process.env.REACT_APP_MAPBOX_ACCESS_TOKEN;

/**
 * ClusterMap - Declarative map component with clustering using react-map-gl
 * Uses HTML Markers for individual listings (clean popups) and Mapbox layers for clusters
 */
const ClusterMap = props => {
  const {
    bounds,
    center,
    listings = [],
    activeListingId,
    onListingClicked,
    onMapLoad,
  } = props;

  // Calculate initial viewport helper function
  const getInitialViewState = () => {
    if (center) {
      return {
        longitude: center.lng,
        latitude: center.lat,
        zoom: 10,
      };
    }

    if (bounds) {
      const ne = bounds.ne;
      const sw = bounds.sw;
      const centerLat = (ne.lat + sw.lat) / 2;
      const centerLng = (ne.lng + sw.lng) / 2;

      // Calculate zoom level based on bounds
      const latDiff = Math.abs(ne.lat - sw.lat);
      const lngDiff = Math.abs(ne.lng - sw.lng);
      const maxDiff = Math.max(latDiff, lngDiff);
      
      let zoom = 5;
      if (maxDiff > 10) zoom = 4;
      else if (maxDiff > 5) zoom = 5;
      else if (maxDiff > 2) zoom = 6;
      else if (maxDiff > 1) zoom = 7;
      else zoom = 8;

      return {
        longitude: centerLng,
        latitude: centerLat,
        zoom,
      };
    }

    // Default to Finland
    return {
      longitude: 26.0,
      latitude: 64.8,
      zoom: 4.2,
    };
  };

  const [viewState, setViewState] = useState(getInitialViewState());
  const [cursor, setCursor] = useState('grab');
  const [isReady, setIsReady] = useState(false);
  const [visibleListings, setVisibleListings] = useState([]);
  const [userLocation, setUserLocation] = useState(null);
  const mapRef = React.useRef(null);

  // Ensure DOM is ready
  useEffect(() => {
    setIsReady(true);
  }, []);

  // Request user's geolocation
  useEffect(() => {
    if (navigator.geolocation) {
      navigator.geolocation.getCurrentPosition(
        (position) => {
          setUserLocation({
            lat: position.coords.latitude,
            lng: position.coords.longitude,
          });
        },
        (error) => {
          // Silently handle errors (user denied permission or geolocation unavailable)
          console.log('Geolocation not available:', error.message);
        },
        { enableHighAccuracy: true, timeout: 5000, maximumAge: 60000 }
      );
    }
  }, []);

  // Delay marker rendering to show map first
  const [showMarkers, setShowMarkers] = useState(false);
  useEffect(() => {
    // Show markers with slight delay so map + clusters render first
    const timer = setTimeout(() => {
      setShowMarkers(true);
    }, 100);
    return () => clearTimeout(timer);
  }, []);

  // Update viewport when bounds change
  useEffect(() => {
    if (bounds || center) {
      setViewState(getInitialViewState());
    }
  }, [bounds, center]);

  // Update visible (unclustered) listings when map moves or zoom changes
  const updateVisibleListings = useCallback(() => {
    const map = mapRef.current?.getMap();
    if (!map || !map.getSource('listings')) return;

    const mapBounds = map.getBounds();
    
    // Get unclustered features that are visible
    const features = map.querySourceFeatures('listings', {
      filter: ['!', ['has', 'point_count']]
    });

    // Filter to only visible features and dedupe
    const seen = new Set();
    const visible = features
      .filter(f => {
        const id = f.properties.id;
        if (seen.has(id)) return false;
        seen.add(id);
        
        const [lng, lat] = f.geometry.coordinates;
        return mapBounds.contains([lng, lat]);
      })
      .map(f => ({
        id: f.properties.id,
        title: f.properties.title,
        companyName: f.properties.companyName,
        coordinates: f.geometry.coordinates
      }));

    setVisibleListings(visible);
  }, []);

  // Convert listings to GeoJSON FeatureCollection
  const convertListingsToGeoJSON = listings => {
    const features = listings
      .filter(l => l?.attributes?.geolocation)
      .map(l => {
        const { lat, lng } = l.attributes.geolocation;
        const title = l.attributes.title || '';
        const companyName = title.length > 20 ? title.substring(0, 18) + '...' : title;

        return {
          type: 'Feature',
          properties: {
            id: l.id.uuid,
            title,
            companyName,
            cluster: false,
          },
          geometry: {
            type: 'Point',
            coordinates: [lng, lat],
          },
        };
      });

    return {
      type: 'FeatureCollection',
      features,
    };
  };

  const listingsData = convertListingsToGeoJSON(listings);

  if (!isReady) {
    return <div className={css.mapContainer} />;
  }

  const handleMapLoad = event => {
    const map = event.target;

    // Set up cursor handlers
    map.on('mouseenter', 'clusters', () => setCursor('pointer'));
    map.on('mouseleave', 'clusters', () => setCursor('grab'));

    // Update visible listings on source data load
    map.on('sourcedata', (e) => {
      if (e.sourceId === 'listings' && e.isSourceLoaded) {
        updateVisibleListings();
      }
    });

    if (onMapLoad) {
      onMapLoad(map);
    }
  };

  const handleMoveEnd = () => {
    updateVisibleListings();
  };

  // Handle cluster clicks
  const handleMapClick = event => {
    const map = mapRef.current?.getMap();
    if (!map) return;

    const point = event.point;
    const features = map.queryRenderedFeatures(point, { layers: ['clusters'] });
    
    if (features.length > 0) {
      const feature = features[0];
      const clusterId = feature.properties.cluster_id;
      const mapboxSource = map.getSource('listings');
      
      if (mapboxSource && mapboxSource.getClusterExpansionZoom) {
        mapboxSource.getClusterExpansionZoom(clusterId, (err, zoom) => {
          if (err) return;
          map.easeTo({
            center: feature.geometry.coordinates,
            zoom,
            duration: 500,
          });
        });
      }
    }
  };

  // Handle marker click
  const handleMarkerClick = (listingId) => {
    const listing = listings.find(l => l.id.uuid === listingId);
    if (listing && onListingClicked) {
      onListingClicked(listing);
    }
  };

  // Cluster layer configuration
  const clusterLayer = {
    id: 'clusters',
    type: 'circle',
    source: 'listings',
    filter: ['has', 'point_count'],
    paint: {
      'circle-color': '#4a90e2',
      'circle-radius': ['step', ['get', 'point_count'], 20, 10, 25, 30, 30, 50, 35],
      'circle-stroke-width': 3,
      'circle-stroke-color': '#ffffff',
    },
  };

  const clusterCountLayer = {
    id: 'cluster-count',
    type: 'symbol',
    source: 'listings',
    filter: ['has', 'point_count'],
    layout: {
      'text-field': '{point_count_abbreviated}',
      'text-font': ['DIN Offc Pro Medium', 'Arial Unicode MS Bold'],
      'text-size': 14,
    },
    paint: {
      'text-color': '#ffffff',
    },
  };

  // Hidden layer just for clustering logic (markers are rendered via React)
  const unclusteredPointLayer = {
    id: 'unclustered-point',
    type: 'circle',
    source: 'listings',
    filter: ['!', ['has', 'point_count']],
    paint: {
      'circle-radius': 0,
      'circle-opacity': 0,
    },
  };

  return (
    <div className={css.mapContainer}>
      <Map
        ref={mapRef}
        {...viewState}
        onMove={evt => setViewState(evt.viewState)}
        onMoveEnd={handleMoveEnd}
        mapStyle="mapbox://styles/mapbox/streets-v11"
        mapboxAccessToken={MAPBOX_TOKEN}
        interactiveLayerIds={['clusters']}
        cursor={cursor}
        onLoad={handleMapLoad}
        onClick={handleMapClick}
        style={{ width: '100%', height: '100%' }}
      >
        <Source
          id="listings"
          type="geojson"
          data={listingsData}
          cluster={true}
          clusterMaxZoom={16}
          clusterRadius={80}
        >
          <Layer {...clusterLayer} />
          <Layer {...clusterCountLayer} />
          <Layer {...unclusteredPointLayer} />
        </Source>

        {/* React Markers for unclustered listings - clean HTML/CSS popups */}
        {/* Render markers only after map is ready (showMarkers = true) */}
        {showMarkers && visibleListings.map(listing => (
          <Marker
            key={listing.id}
            longitude={listing.coordinates[0]}
            latitude={listing.coordinates[1]}
            anchor="bottom"
            onClick={(e) => {
              e.originalEvent.stopPropagation();
              handleMarkerClick(listing.id);
            }}
          >
            <div className={css.markerContainer}>
              <div className={css.markerLabel}>
                {listing.companyName}
              </div>
              <div className={css.markerPin}>
                <div className={css.markerPinInner} />
              </div>
            </div>
          </Marker>
        ))}

        {/* User's GPS location marker (blue dot) */}
        {userLocation && (
          <Marker
            longitude={userLocation.lng}
            latitude={userLocation.lat}
            anchor="center"
          >
            <div className={css.userLocationMarker}>
              <div className={css.userLocationPulse} />
              <div className={css.userLocationDot} />
            </div>
          </Marker>
        )}
      </Map>
    </div>
  );
};

export default ClusterMap;

